import React, { useState, useEffect, useRef } from "react";
import "../../../assets/css/Chatbot.css";
import chatIcon from "assets/img/chat.png";
import { environments } from "constants/environments";
import { useAuth } from "contexts/auth";

const Chatbot: React.FC = () => {
  const [messages, setMessages] = useState<{ sender: string; text: string }[]>(
    []
  );
  const [input, setInput] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [chatOpen, setChatOpen] = useState<boolean>(false);
  const [botTyping, setBotTyping] = useState<string>("");
  const abortControllerRef = useRef<AbortController | null>(null);
  const { sharedData } = useAuth();

  // const API = useMemo(() => process.env.REACT_APP_API_KEY, []);
  const chatAPI = environments.domain + sharedData?.apisList.Program.Chat;

  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem("chatMessages", JSON.stringify(messages));
    }
  }, [messages]);

  useEffect(() => {
    const savedMessages = localStorage.getItem("chatMessages");
    if (savedMessages) {
      setMessages(JSON.parse(savedMessages));
    }
  }, []);

  const typeText = (text: string) => {
    return new Promise<void>((resolve) => {
      let index = 0;
      setBotTyping("");
      const interval = setInterval(() => {
        if (index < text.length) {
          setBotTyping((prev) => prev + text[index]);
          index++;
        } else {
          clearInterval(interval);
          resolve();
        }
      }, 50);
    });
  };

  const sendMessage = async (message: string) => {
    const userMessage = { sender: "user", text: message };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setLoading(true);
    setBotTyping("");

    // ✅ Abort any previous request if a new message is sent
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await fetch(chatAPI, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token: localStorage.getItem(environments?.tokenKey) || "",
        },
        body: JSON.stringify({ message }),
        signal: controller.signal, // 🔹 Supports stopping response
      });

      if (!response.body) {
        throw new Error("Response body is empty");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let botMessage = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true }).trim();

        if (chunk) {
          try {
            let aString = String(chunk)
              .replaceAll('"', "")
              .replaceAll("[DONE]", "")
              .replaceAll("undefined", "")
              .replaceAll("null", "")
              .replaceAll("\\n", "\n");

            botMessage += aString;
            setBotTyping(botMessage);
          } catch (error) {
            console.error("Skipping invalid JSON:", chunk);
          }
        }
      }

      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: "bot", text: botMessage },
      ]);
    } catch (error: any) {
      if (error.name === "AbortError") {
        setMessages((prevMessages) => [
          ...prevMessages,
          { sender: "bot", text: "Response stopped." },
        ]);
      } else {
        console.error("Error fetching chatbot response:", error);
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            sender: "bot",
            text: "Sorry, something went wrong. Please try again.",
          },
        ]);
      }
    }

    setLoading(false);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (input.trim()) {
      sendMessage(input);
      setInput("");
    }
  };

  const toggleChat = () => {
    setChatOpen((prev) => !prev);
  };

  const clearChat = () => {
    setMessages([]);
    localStorage.removeItem("chatMessages");
  };

  const stopResponse = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const OpenChat = ({
    chatOpen,
    toggleChat,
  }: {
    chatOpen: boolean;
    toggleChat: () => void;
  }) => {
    return (
      <div
        className={`chat-icon ${chatOpen ? "open" : ""}`}
        onClick={toggleChat}
      >
        <div className="semicircle-text">
          <span
            className="char"
            style={{
              transform: "rotate(-85deg)",
            }}
          >
            Y
          </span>
          <span
            className="char"
            style={{
              transform: "rotate(-75deg)",
            }}
          >
            o
          </span>
          <span
            className="char"
            style={{
              transform: "rotate(-65deg)",
            }}
          >
            u
          </span>
          <span
            className="char"
            style={{
              transform: "rotate(-30deg)",
            }}
          >
            {" "}
          </span>
          <span
            className="char"
            style={{
              transform: "rotate(-47deg)",
            }}
          >
            C
          </span>
          <span
            className="char"
            style={{
              transform: "rotate(-33deg)",
            }}
          >
            a
          </span>
          <span
            className="char"
            style={{
              transform: "rotate(-22deg)",
            }}
          >
            n
          </span>
          <span
            className="char"
            style={{
              transform: "rotate(-21deg)",
            }}
          >
            {" "}
          </span>
          <span
            className="char"
            style={{
              transform: "rotate(-3deg)",
            }}
          >
            C
          </span>
          <span
            className="char"
            style={{
              transform: "rotate(10deg)",
            }}
          >
            h
          </span>
          <span
            className="char"
            style={{
              transform: "rotate(21deg)",
            }}
          >
            a
          </span>
          <span
            className="char"
            style={{
              transform: "rotate(31deg)",
            }}
          >
            t
          </span>
          <span
            className="char"
            style={{
              transform: "rotate(150deg)",
            }}
          >
            {" "}
          </span>
          <span
            className="char"
            style={{
              transform: "rotate(45deg)",
            }}
          >
            H
          </span>
          <span
            className="char"
            style={{
              transform: "rotate(57deg)",
            }}
          >
            e
          </span>
          <span
            className="char"
            style={{
              transform: "rotate(68deg)",
            }}
          >
            r
          </span>
          <span
            className="char"
            style={{
              transform: "rotate(74deg)",
            }}
          >
            e
          </span>
        </div>

        <div className="icon">
          <img src={chatIcon} alt="chat icon" />
        </div>
      </div>
    );
  };

  return (
    <div className="chatbot-container">
      <OpenChat chatOpen={chatOpen} toggleChat={toggleChat}></OpenChat>

      {chatOpen && (
        <div className="chat-box">
          <div className="messages">
            {messages.map((msg, index) => (
              <div key={index} className={`message ${msg.sender}`}>
                {msg.text.split("\n").map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </div>
            ))}
            {loading && botTyping && (
              <div className="message bot">
                {botTyping.split("\n").map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </div>
            )}
            {loading && !botTyping && (
              <div className="message bot">Typing...</div>
            )}
          </div>
          <form onSubmit={handleSubmit}>
            <input
              className="input-chat"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type a message..."
              disabled={loading}
            />
            <button type="submit" disabled={loading} className="send-button">
              Send
            </button>
          </form>
          <div className="text-right mt-2">
            <button
              type="button"
              onClick={clearChat}
              className="clear-chat-button"
            >
              Clear Chat
            </button>
            {loading && (
              <button
                type="button"
                onClick={stopResponse}
                className="stop-response-button"
              >
                Stop
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;
